import React, { useState, useMemo } from 'react';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import Can from '../../components/Can';
import ConfirmModal from '../../components/ConfirmModal';
import './Overtime.css';

const STATUS_TABS = ['Pending', 'Approved', 'Paid', 'Rejected', 'All'];

const EMPTY = {
  employee_id: '', ot_date: today(), hours: '', rate_per_hr: '', reason: '',
};

function OTStatusPill({ status }) {
  return (
    <span className={`ot-status ot-status-${(status || '').toLowerCase()}`}>{status}</span>
  );
}

function Kpi({ label, value, sub, tone }) {
  return (
    <div className={`ot-kpi ${tone ? 'kpi-' + tone : ''}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

export default function Overtime() {
  const [status, setStatus] = useState('Pending');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [payDraft, setPayDraft] = useState(null);
  const [rejectDraft, setRejectDraft] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [msg, setMsg] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  const { data, loading, error, reload } = useApi(
    () => api.overtime.list(status === 'All' ? null : status),
    [status]
  );
  const { data: employees } = useApi(() => api.employees.list(), [], []);
  const { data: banksData } = useApi(() => api.banks.list(), [], { banks: [] });
  const banks = banksData.banks || [];

  const activeEmps = useMemo(
    () => (employees || []).filter(e => Number(e.is_active) === 1),
    [employees]
  );

  const selectedEmp = activeEmps.find(e => String(e.id) === String(form.employee_id));

  // Auto-fill rate when employee is picked
  React.useEffect(() => {
    if (selectedEmp && !form.rate_per_hr) {
      setForm(f => ({ ...f, rate_per_hr: selectedEmp.ot_rate_per_hr || '' }));
    }
  }, [form.employee_id]);

  const computedAmount = useMemo(() => {
    const h = parseFloat(form.hours || 0);
    const r = parseFloat(form.rate_per_hr || 0);
    return (h > 0 && r > 0) ? h * r : 0;
  }, [form.hours, form.rate_per_hr]);

  const handleSave = async () => {
    if (!form.employee_id) { notify('❌ Select employee'); return; }
    if (!form.ot_date) { notify('❌ Pick date'); return; }
    const h = parseFloat(form.hours || 0);
    const r = parseFloat(form.rate_per_hr || 0);
    if (h <= 0 || h > 24) { notify('❌ Hours must be 0–24'); return; }
    if (r <= 0) { notify('❌ Rate must be > 0'); return; }

    setSaving(true);
    try {
      await api.overtime.create({
        employee_id: parseInt(form.employee_id),
        ot_date: form.ot_date,
        hours: h,
        rate_per_hr: r,
        reason: form.reason.trim(),
      });
      notify(`✅ OT recorded: ₹${fmtNum(computedAmount)}`);
      setForm(EMPTY); setShowForm(false);
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleApprove = async (id) => {
    try {
      await api.overtime.approve(id);
      notify('✅ Approved');
      reload();
    } catch (e) { notify('❌ ' + e.message); }
  };

  const handlePay = async () => {
    try {
      await api.overtime.pay(payDraft.id, {
        mode: payDraft.mode,
        paid_ref: payDraft.paid_ref || '',
      });
      notify(`✅ Paid ₹${payDraft.amount} via ${payDraft.mode}`);
      setPayDraft(null);
      reload();
    } catch (e) { notify('❌ ' + e.message); }
  };

  const handleReject = async () => {
    try {
      await api.overtime.reject(rejectDraft.id, rejectDraft.reason);
      notify('✅ Rejected');
      setRejectDraft(null);
      reload();
    } catch (e) { notify('❌ ' + e.message); }
  };

  const handleDelete = async () => {
    try {
      await api.overtime.remove(confirmDelete.id);
      notify('✅ Deleted');
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setConfirmDelete(null);
  };

  const totals = data?.totals || {};

  return (
    <div className="ot-page">
      <div className="page-header">
        <h2 className="page-title">Employee Overtime</h2>
        <Can module="overtime" action="create">
          <button className="btn btn-primary btn-sm"
                  onClick={() => { setShowForm(!showForm); setForm(EMPTY); }}>
            {showForm ? '✕ Cancel' : '＋ Record OT'}
          </button>
        </Can>
      </div>

      {msg && <div className="ot-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}

      {/* KPIs */}
      <div className="ot-kpis">
        <Kpi label="Pending Approval"  value={`₹${fmtNum(totals.pending_amt || 0)}`}
             sub={`${totals.pending_hrs || 0} hrs`} tone="orange" />
        <Kpi label="Approved (unpaid)" value={`₹${fmtNum(totals.approved_amt || 0)}`} tone="blue" />
        <Kpi label="Paid This Period"  value={`₹${fmtNum(totals.paid_amt || 0)}`} tone="green" />
      </div>

      {/* Status tabs */}
      <div className="ot-tabs">
        {STATUS_TABS.map(t => (
          <button key={t} className={`ot-tab ${status === t ? 'active' : ''}`}
                  onClick={() => setStatus(t)}>{t}</button>
        ))}
      </div>

      {/* New OT form */}
      {showForm && (
        <div className="section-card">
          <div className="section-header">Record OT Hours</div>
          <div className="ot-form-grid">
            <div className="form-group">
              <label>Employee *</label>
              <select value={form.employee_id}
                      onChange={e => setForm({ ...form, employee_id: e.target.value, rate_per_hr: '' })}>
                <option value="">-- Select --</option>
                {activeEmps.map(e => (
                  <option key={e.id} value={e.id}>
                    {e.emp_code} — {e.name} (₹{fmtNum(e.ot_rate_per_hr)}/hr)
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Date *</label>
              <input type="date" value={form.ot_date}
                     onChange={e => setForm({ ...form, ot_date: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Hours *</label>
              <input type="number" step="0.25" value={form.hours}
                     onChange={e => setForm({ ...form, hours: e.target.value })}
                     placeholder="e.g. 3.5" />
            </div>
            <div className="form-group">
              <label>Rate per hour (₹)</label>
              <input type="number" step="0.01" value={form.rate_per_hr}
                     onChange={e => setForm({ ...form, rate_per_hr: e.target.value })} />
            </div>
            <div className="form-group ot-wide">
              <label>Reason</label>
              <input value={form.reason}
                     onChange={e => setForm({ ...form, reason: e.target.value })}
                     placeholder="e.g. Inventory stocktake, heavy delivery day" />
            </div>
          </div>

          {computedAmount > 0 && (
            <div className="ot-amount-preview">
              {form.hours} hrs × ₹{form.rate_per_hr} = <strong>₹{fmtNum(computedAmount)}</strong>
            </div>
          )}

          <div style={{ padding: '0 12px 12px' }}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
              {saving ? '⏳ Saving...' : 'Submit for Approval'}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <div className="section-header">
            {status === 'All' ? 'All OT Entries' : `${status} OT Entries`}
            {' '}({data?.rows?.length || 0})
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Date</th>
                <th style={{ textAlign: 'left' }}>Employee</th>
                <th style={{ textAlign: 'right' }}>Hours</th>
                <th style={{ textAlign: 'right' }}>Rate</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ textAlign: 'left' }}>Reason</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>

            </thead>
            <tbody>
              {(!data?.rows || data.rows.length === 0) ? (
                <tr><td colSpan="8" style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>
                  No {status.toLowerCase()} entries
                </td></tr>
              ) : data.rows.map(r => (
                <tr key={r.id}>
                  <td style={{ textAlign: 'left' }}>{fmtDate(r.ot_date)}</td>
                  <td style={{ textAlign: 'left', minWidth: '150px' }}>
                    <div style={{ fontWeight: 600 }}>{r.employee_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                      {r.emp_code}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmtNum(r.hours)}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>
                    ₹{fmtNum(r.rate_per_hr)}
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>
                    ₹ {fmtNum(r.amount)}
                  </td>

                  <td style={{ textAlign: 'left', fontSize: 12 }}>
                    {r.reason || '—'}
                    {r.status === 'Rejected' && r.reject_reason && (
                      <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 2 }}>
                        Rejected: {r.reject_reason}
                      </div>
                    )}
                    {r.status === 'Paid' && r.paid_at && (
                      <div style={{ fontSize: 11, color: 'var(--secondary)', marginTop: 2 }}>
                        Paid {fmtDate(r.paid_at.split(' ')[0])} via {r.paid_mode}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <OTStatusPill status={r.status} />
                  </td>
                  <td style={{ textAlign: 'center' }}>

                    <div className="ot-actions">
                      {r.status === 'Pending' && (
                        <>
                          <Can module="overtime" action="approve">
                            <button className="btn btn-sm" style={{ color: 'var(--secondary)' }}
                                    onClick={() => handleApprove(r.id)}>✓ Approve</button>
                          </Can>
                          <Can module="overtime" action="approve">
                            <button className="btn btn-sm" style={{ color: 'var(--danger)' }}
                                    onClick={() => setRejectDraft({ id: r.id, reason: '' })}>✕ Reject</button>
                          </Can>
                          <Can module="overtime" action="delete">
                            <button className="btn btn-sm"
                                    onClick={() => setConfirmDelete(r)}>🗑</button>
                          </Can>
                        </>
                      )}
                      {r.status === 'Approved' && (
                        <Can module="overtime" action="approve">
                          <button className="btn btn-primary btn-sm"
                                  onClick={() => setPayDraft({ id: r.id, amount: r.amount, mode: 'Cash', paid_ref: '' })}>
                            💰 Pay
                          </button>
                        </Can>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pay modal */}
      {payDraft && (
        <div className="ot-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setPayDraft(null); }}>
          <div className="ot-modal">
            <h3>Pay OT — ₹{fmtNum(payDraft.amount)}</h3>
            <div className="form-group">
              <label>Payment Mode</label>
              <select value={payDraft.mode}
                      onChange={e => setPayDraft({ ...payDraft, mode: e.target.value })}>
                <option value="Cash">💵 Cash</option>
                {banks.map(b => (
                  <option key={b.id} value={`Bank:${b.id}`}>🏦 {b.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Reference (optional)</label>
              <input value={payDraft.paid_ref}
                     onChange={e => setPayDraft({ ...payDraft, paid_ref: e.target.value })}
                     placeholder="Cheque #, UPI ref, etc." />
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Posts: <strong>Dr Expense (Overtime)</strong> → <strong>Cr {payDraft.mode === 'Cash' ? 'Cash' : banks.find(b => `Bank:${b.id}` === payDraft.mode)?.name || 'Bank'}</strong>
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
              <button className="btn btn-sm" onClick={() => setPayDraft(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handlePay}>Confirm Payment</button>
            </div>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejectDraft && (
        <div className="ot-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setRejectDraft(null); }}>
          <div className="ot-modal">
            <h3>Reject OT</h3>
            <div className="form-group">
              <label>Reason *</label>
              <textarea rows={3} value={rejectDraft.reason}
                        onChange={e => setRejectDraft({ ...rejectDraft, reason: e.target.value })}
                        style={{ width: '100%', padding: 8, border: '1px solid var(--border)', borderRadius: 4 }}
                        placeholder="Why are you rejecting?" />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
              <button className="btn btn-sm" onClick={() => setRejectDraft(null)}>Cancel</button>
              <button className="btn btn-sm" style={{ color: 'var(--danger)' }}
                      disabled={!rejectDraft.reason.trim()} onClick={handleReject}>
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Delete OT Entry?"
        message={confirmDelete
          ? `Delete pending OT for ${confirmDelete.employee_name} on ${fmtDate(confirmDelete.ot_date)}?`
          : ''}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
        confirmLabel="Delete"
      />
    </div>
  );
}

